import React from 'react';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import LoadingScreen from './components/LoadingScreen';
import Configuration from './components/Configuration';
import Planner from './components/Planner';
import {
  selectShowLoadingScreen,
  selectShowConfiguration,
  selectShowPlanner,
} from './store/configSlice';
import { useAppSelector } from 'store';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#667eea',
      light: '#8b9df6',
      dark: '#4c5ec7',
    },
    secondary: {
      main: '#764ba2',
      light: '#9870bb',
      dark: '#573681',
    },
    success: {
      main: '#4CAF50',
      light: '#81C784',
      dark: '#388E3C',
    },
    error: {
      main: '#F44336',
      light: '#E57373',
      dark: '#D32F2F',
    },
    warning: {
      main: '#FFC107',
      light: '#FFD54F',
      dark: '#FFA000',
    },
    info: {
      main: '#9C27B0',
      light: '#BA68C8',
      dark: '#7B1FA2',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 700,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 500,
    },
    h6: {
      fontWeight: 500,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0px 2px 4px rgba(0,0,0,0.1)',
    '0px 4px 8px rgba(0,0,0,0.12)',
    '0px 6px 12px rgba(0,0,0,0.14)',
    '0px 8px 16px rgba(0,0,0,0.16)',
    '0px 10px 20px rgba(0,0,0,0.18)',
    '0px 12px 24px rgba(0,0,0,0.2)',
    '0px 14px 28px rgba(0,0,0,0.22)',
    '0px 16px 32px rgba(0,0,0,0.24)',
    '0px 18px 36px rgba(0,0,0,0.26)',
    '0px 20px 40px rgba(0,0,0,0.28)',
    '0px 22px 44px rgba(0,0,0,0.3)',
    '0px 24px 48px rgba(0,0,0,0.32)',
    '0px 26px 52px rgba(0,0,0,0.34)',
    '0px 28px 56px rgba(0,0,0,0.36)',
    '0px 30px 60px rgba(0,0,0,0.38)',
    '0px 32px 64px rgba(0,0,0,0.4)',
    '0px 34px 68px rgba(0,0,0,0.42)',
    '0px 36px 72px rgba(0,0,0,0.44)',
    '0px 38px 76px rgba(0,0,0,0.46)',
    '0px 40px 80px rgba(0,0,0,0.48)',
    '0px 42px 84px rgba(0,0,0,0.5)',
    '0px 44px 88px rgba(0,0,0,0.52)',
    '0px 46px 92px rgba(0,0,0,0.54)',
    '0px 48px 96px rgba(0,0,0,0.56)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '10px 24px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 4px 12px rgba(0,0,0,0.15)',
          },
        },
        contained: {
          '&:active': {
            boxShadow: '0px 2px 8px rgba(0,0,0,0.2)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0px 4px 12px rgba(0,0,0,0.08)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 12,
        },
      },
    },
  },
});

const App: React.FC = () => {
  const showLoadingScreen = useAppSelector(selectShowLoadingScreen);
  const showConfiguration = useAppSelector(selectShowConfiguration);
  const showPlanner = useAppSelector(selectShowPlanner);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {showLoadingScreen && <LoadingScreen />}
      {showConfiguration && <Configuration />}
      {showPlanner && <Planner />}
    </ThemeProvider>
  );
};

export default App;
